//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  RightsRequestCommunicationType,
  RightsRequestMessageSource,
} from '@apis/rights-management/rightsManagementApiModels';
import Cuid from '@models/Cuid';
import { isDefined } from '@utils/filterPredicates';

import { Locales } from 'app/constants/locales/locale';

const INSTAGRAM_OAUTH2_AUTHORIZE_URL =
  'https://api.instagram.com/oauth/authorize';

export const enum OAuthLandingPageMessageType {
  OAuthCheckPassed = 'RightsRequestOAuthCheckPassed',
  OAuthCancelled = 'RightsRequestOAuthCancel',
  OAuthCheckFailed = 'RightsRequestOAuthCheckFailed',
}

export interface OAuthLandingPageSuccessMessage {
  type:
    | OAuthLandingPageMessageType.OAuthCancelled
    | OAuthLandingPageMessageType.OAuthCheckPassed;
}

export interface OAuthLandingPageErrorMessage {
  type: OAuthLandingPageMessageType.OAuthCheckFailed;
  error: string;
}

export type OAuthLandingPagePostMessage =
  | OAuthLandingPageSuccessMessage
  | OAuthLandingPageErrorMessage;

export interface RequestRightsOAuthParams {
  approvalPhrase?: string;
  communicationType?: RightsRequestCommunicationType;
  cuid: Cuid;
  requestMessage?: string;
  requestMessageLanguage?: Locales;
  requestMessageSource?: RightsRequestMessageSource;
  targetUrl?: string;
}

export const getInstagramOAuthUrl = (itemParams: RequestRightsOAuthParams) => {
  const itemParamString = encodeURIComponent(JSON.stringify(itemParams));
  const redirectUri = `${location.origin}/rights-request-oauth-landing`;

  const params = new URLSearchParams({
    client_id: CURALATE_ENV.INSTAGRAM_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'user_profile',
    state: itemParamString,
  });

  return `${INSTAGRAM_OAUTH2_AUTHORIZE_URL}?${params.toString()}`;
};

export const getTikTokOAuthUrl = (itemParams: RequestRightsOAuthParams) => {
  const itemParamString = encodeURIComponent(JSON.stringify(itemParams));
  const redirectUri = `${location.origin}/rights-request-oauth-landing`;

  // For TikTok rights requests, always use autoRedirect to ensure seamless flow:
  // 1. Landing page initiates rights request via API
  // 2. Landing page immediately redirects to TikTok video URL
  // 3. User can post their approval comment on TikTok
  return `${redirectUri}?state=${itemParamString}&autoRedirect=true`;
};

export const isOAuthMessage = (type?: OAuthLandingPageMessageType) => {
  if (!isDefined(type)) {
    return false;
  }

  switch (type) {
    case OAuthLandingPageMessageType.OAuthCheckPassed:
    case OAuthLandingPageMessageType.OAuthCancelled:
    case OAuthLandingPageMessageType.OAuthCheckFailed:
      return true;
    default:
      return false;
  }
};
